// Iter88 / Phase 3H — Controlled CFM-CC-SHARES Auto-Scan Panel.
//
// First CFM controlled auto-scan. Combines:
//   * fresh market snapshot call chain from MarketSnapshotContext
//   * operator-provided shares inventory context from
//     CFMInventoryContext
// and POSTs them to the existing Iter64 CFM Shares preview
// endpoint:
//
//   POST /api/forge/toolbox/scanner/cfm-shares-preview
//
// Results render as Wheel-style cards via StrategyResultCard
// plus a small inline CFM Shares metric block (short call strike,
// expiration, DTE, credit, extrinsic value, adjusted basis,
// assignment exit price, assignment P/L per share, premium
// cushion %).
//
// HARD CONSTRAINTS (asserted by Iter88 tests):
//  * Manual-run only. No `useEffect`. No auto-fire on mount.
//  * No outbound HTTP except the CFM Shares preview endpoint.
//  * Never calls `/forge/candidates`, `/market-scanner/*`,
//    snapshot build endpoint, `/scanner/bpcs-preview`,
//    `/scanner/bccs-preview`, `/scanner/iron-condor-preview`,
//    `/scanner/cfm-leaps-preview`, broker / account / position /
//    order / journal / admin endpoints.
//  * No `localStorage` / `sessionStorage` / `IndexedDB` / cookies.
//  * No ranking / winner / best / preferred / auto-select
//    language outside safety disclaimers.

import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronDown, ChevronUp, AlertTriangle, Search, CheckCircle2,
  Clock,
} from "lucide-react";
import { API } from "@/App";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import {
  useCFMInventory, isSharesReady,
} from "@/contexts/CFMInventoryContext";
import { useWESContext } from "@/contexts/WESContextProvider";
import {
  summarizeWesContext, applyWesToSubpayload,
} from "@/components/wesContextPayloadHelpers";
import WesProvenanceStrip from "@/components/WesProvenanceStrip";
import StrategyResultCard from "@/components/StrategyResultCard";
import { MetricChip } from "@/components/ScannerResultNormalization";
import SnapshotScanKit from "@/components/SnapshotScanKit";

const CFM_SHARES_ENDPOINT = "/forge/toolbox/scanner/cfm-shares-preview";
const STALE_SECONDS = 5 * 60;
const DEFAULT_MAX_SYMBOLS = 5;
const TID = "cfm-shares-auto-scan";

function ageSeconds(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

function numOrNull(s) {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const SAFETY_LABELS = [
  { testid: `${TID}-banner-cfm-shares-only`,    text: "CFM Shares only." },
  { testid: `${TID}-banner-manual`,             text: "Manual scan only." },
  { testid: `${TID}-banner-no-ranking`,         text: "No ranking." },
  { testid: `${TID}-banner-no-winner`,          text: "No winner." },
  { testid: `${TID}-banner-no-execution`,       text: "No trade execution." },
  { testid: `${TID}-banner-no-journal`,         text: "No journal write." },
  { testid: `${TID}-banner-no-persistence`,     text: "No persistence." },
  { testid: `${TID}-banner-no-account-fetch`,   text: "No account / position fetch." },
];

function chainCounts(row) {
  const chain = Array.isArray(row?.chain) ? row.chain : [];
  return {
    puts:  chain.filter(r => r && r.type === "put").length,
    calls: chain.filter(r => r && r.type === "call").length,
  };
}

function buildAccountContext(invRow) {
  if (!invRow) return null;
  const s = invRow.shares || {};
  return {
    shares_owned:        numOrNull(s.shares_owned),
    share_basis:         numOrNull(s.share_basis),
    total_call_credits:  numOrNull(s.total_call_credits),
    minimum_exit_price:  numOrNull(s.minimum_exit_price),
    assignment_policy:   s.shares_assignment_policy || null,
    notes:               s.notes || null,
  };
}

function buildPerSymbolPayload(row, invRow, wesRow, wesMeta) {
  const warnings = [];
  const chain = Array.isArray(row.chain) ? row.chain : [];
  const calls = chain.filter(r => r && r.type === "call");
  if (calls.length === 0) {
    warnings.push("Missing call chain — CFM Shares unavailable.");
  }
  if (!invRow || !isSharesReady(invRow)) {
    warnings.push("Inventory context required — CFM Shares unavailable.");
  }

  const sub = {
    spot_price: row.spot_price ?? null,
    chain: calls,
    wes: {},
    account_context: buildAccountContext(invRow) || {},
  };
  // Iter92 — helper emits the existing
  //   "WES context unavailable — CFM Shares scan may be structurally incomplete."
  // copy if the envelope has no row for this symbol.
  const wesWarnings = applyWesToSubpayload(
    sub, wesRow, wesMeta, "CFM Shares"
  );
  wesWarnings.forEach(w => warnings.push(w));
  if (row.ivr != null) sub.ivr = row.ivr;

  sub.snapshot_meta = {
    snapshot_id: row.__snapshot_id || null,
    snapshot_as_of: row.__snapshot_as_of || null,
    source: row.source || null,
    chosen_expiration: row.chosen_expiration || null,
    dte: row.dte ?? null,
    freshness_age_seconds: ageSeconds(row.__snapshot_as_of),
    warnings,
  };
  return { payload: sub, warnings };
}

export default function CFMSharesAutoScanPanel() {
  const { snapshot } = useMarketSnapshot();
  const { rows: invRows, metadata: invMetadata } = useCFMInventory();
  const { wesContext } = useWESContext();
  const wesSummary = summarizeWesContext(wesContext);
  const wesMeta = {
    wesAsOf: wesSummary.asOf,
    fingerprint: wesSummary.fingerprint,
    version: wesSummary.version,
    stale: wesSummary.stale,
  };

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [maxSymbols, setMaxSymbols] = useState(String(DEFAULT_MAX_SYMBOLS));
  const [minIvr, setMinIvr] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [lastScannedAt, setLastScannedAt] = useState(null);
  const [perSymbolWarnings, setPerSymbolWarnings] = useState({});

  const rows = useMemo(() => {
    if (!snapshot || !Array.isArray(snapshot.rows)) return [];
    return snapshot.rows.filter(r => r && r.symbol);
  }, [snapshot]);

  const seedKey = `${snapshot?.snapshot_id || ""}|${invMetadata?.context_updated_at || ""}`;
  const [seededFor, setSeededFor] = useState("");
  if (seedKey !== "|" && seedKey !== seededFor) {
    const next = {};
    rows.forEach(r => {
      const inv = invRows[r.symbol];
      const { calls } = chainCounts(r);
      // Default-select symbols that are both call-chain-available
      // AND have ready shares inventory context. Re-seeds when the
      // snapshot changes or when the inventory context is edited.
      next[r.symbol] = calls > 0 && !!inv && isSharesReady(inv);
    });
    setSelected(next);
    setSeededFor(seedKey);
  }

  function toggleSymbol(sym) { setSelected(p => ({ ...p, [sym]: !p[sym] })); }
  function selectAll() {
    const next = {}; rows.forEach(r => { next[r.symbol] = true; });
    setSelected(next);
  }
  function clearAll() { setSelected({}); }

  function chainAvailability(row) {
    const { puts, calls } = chainCounts(row);
    const inv = invRows[row.symbol];
    const invReady = !!inv && isSharesReady(inv);
    return { ok: calls > 0 && invReady, puts, calls };
  }

  function buildEnvelope() {
    const maxN = Number(maxSymbols);
    const cap = Number.isFinite(maxN) && maxN > 0
      ? Math.floor(maxN) : DEFAULT_MAX_SYMBOLS;
    const minIvrNum = minIvr.trim() === "" ? null : Number(minIvr);
    const picked = rows
      .filter(r => selected[r.symbol])
      .filter(r => minIvrNum == null
        || !Number.isFinite(minIvrNum)
        || (r.ivr != null && r.ivr >= minIvrNum))
      .slice(0, cap);
    const watchlist = picked.map(r => r.symbol);
    const payloads_by_symbol = {};
    const warningsPerSymbol = {};
    picked.forEach(r => {
      const enriched = {
        ...r,
        __snapshot_id: snapshot.snapshot_id,
        __snapshot_as_of: snapshot.as_of,
      };
      const invRow = invRows[r.symbol];
      const { payload, warnings } = buildPerSymbolPayload(
        enriched,
        invRow,
        wesSummary.bySymbol[r.symbol],
        wesMeta,
      );
      payloads_by_symbol[r.symbol] = payload;
      warningsPerSymbol[r.symbol] = warnings;
    });
    return { envelope: { watchlist, payloads_by_symbol },
             warningsPerSymbol, picked };
  }

  async function runScan() {
    setErrorMsg(null);
    if (!snapshot) { setErrorMsg("Fresh snapshot required."); return; }
    const { envelope, warningsPerSymbol, picked } = buildEnvelope();
    if (picked.length === 0) {
      setErrorMsg("Select at least one snapshot symbol with ready inventory context.");
      return;
    }
    setPerSymbolWarnings(warningsPerSymbol);
    setLastRequest(envelope);
    setLoading(true);
    try {
      const r = await axios.post(`${API}${CFM_SHARES_ENDPOINT}`, envelope);
      setResult(r.data);
      setLastScannedAt(new Date().toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e?.message || "Network error");
      setErrorMsg(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  function clearResults() {
    setResult(null); setErrorMsg(null);
    setLastRequest(null); setLastScannedAt(null);
    setPerSymbolWarnings({});
  }

  const invEditedAgeS = ageSeconds(invMetadata?.context_updated_at);

  return (
    <div
      data-testid={`${TID}-panel`}
      className="mb-5 rounded-sm border border-[#22D3CC]/30 bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid={`${TID}-toggle`}
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#22D3CC]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          CFM Shares Controlled Auto-Scan
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 3h · cfm-cc-shares only · manual-run · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {!snapshot && (
            <div
              data-testid={`${TID}-fresh-snapshot-required`}
              className="px-4 py-2 text-[10px] font-mono text-[#FFB800] border-b border-[#26262B] flex items-center gap-2"
            >
              <AlertTriangle size={11} />
              <span>Fresh snapshot required.</span>
            </div>
          )}

          {Object.keys(invRows).length === 0 && (
            <div
              data-testid={`${TID}-inventory-context-missing`}
              className="px-4 py-2 text-[10px] font-mono text-[#FFB800] border-b border-[#26262B] flex items-center gap-2"
            >
              <AlertTriangle size={11} />
              <span>Inventory context required — CFM Shares unavailable.</span>
            </div>
          )}

          {invMetadata?.context_updated_at && (
            <div
              data-testid={`${TID}-inventory-edited-stamp`}
              className="px-4 py-1 text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] border-b border-[#26262B] flex items-center gap-1"
            >
              <Clock size={9} />
              Inventory context last edited
              {" "}{invEditedAgeS != null ? `${invEditedAgeS}s ago` : "—"}
            </div>
          )}

          <SnapshotScanKit
            snapshot={snapshot}
            rows={rows}
            staleSecondsCfg={STALE_SECONDS}
            ageSeconds={ageSeconds}
            selected={selected}
            onToggleSymbol={toggleSymbol}
            onSelectAll={selectAll}
            onClearAll={clearAll}
            chainAvailability={chainAvailability}
            maxSymbols={maxSymbols} setMaxSymbols={setMaxSymbols}
            minIvr={minIvr} setMinIvr={setMinIvr}
            loading={loading} onRun={runScan} onClear={clearResults}
            errorMsg={errorMsg}
            runLabel="Run CFM Shares Auto-Scan"
            lastScannedAt={lastScannedAt}
            testidPrefix={TID}
            safetyLabels={SAFETY_LABELS}
          />

          {snapshot && (
            <WesProvenanceStrip
              moduleId="cfm-shares"
              wesSummary={wesSummary}
            />
          )}

          {snapshot && !loading && !result && !errorMsg && (
            <div
              data-testid={`${TID}-empty`}
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No CFM Shares auto-scan run yet. Pick symbols with
              ready inventory context and click Run CFM Shares
              Auto-Scan.
            </div>
          )}

          {snapshot && result && !loading && !errorMsg && (
            <CFMSharesResult
              result={result}
              lastRequest={lastRequest}
              perSymbolWarnings={perSymbolWarnings}
              snapshot={snapshot}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── CFM Shares result block ────────────────────────────────────

function CFMSharesMetrics({ rec, testidPrefix }) {
  const s = rec?.selected_structure || rec?.option_plan || rec?.selected_candidate;
  if (!s) return null;
  const sym = rec?.symbol || "";
  const tid = suffix => `${testidPrefix}-${suffix}-${sym}`;
  return (
    <div
      data-testid={tid("metrics")}
      className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
    >
      <MetricChip label="Short"        value={s.short_strike ?? s.strike}
                   testid={tid("short-strike")} />
      <MetricChip label="Expiration"   value={s.expiration}
                   testid={tid("expiration")} />
      <MetricChip label="DTE"          value={s.dte}
                   testid={tid("dte")} />
      <MetricChip label="Credit"       value={s.credit ?? s.net_credit}
                   testid={tid("credit")} />
      <MetricChip label="Extrinsic"    value={s.extrinsic_value ?? s.extrinsic}
                   testid={tid("extrinsic")} />
      <MetricChip label="Adj basis"    value={s.adjusted_basis ?? s.adjusted_share_basis}
                   testid={tid("adjusted-basis")} />
      <MetricChip label="Assign exit"  value={s.assignment_exit_price}
                   testid={tid("assignment-exit-price")} />
      <MetricChip label="Assign P/L"   value={s.assignment_pl_per_share ?? s.assignment_profit_per_share}
                   testid={tid("assignment-pl-per-share")} />
      <MetricChip label="Cushion %"    value={s.premium_cushion_pct ?? s.cushion_pct}
                   testid={tid("premium-cushion-pct")} />
    </div>
  );
}

function CFMSharesResult({ result, lastRequest, perSymbolWarnings, snapshot }) {
  const counts = result.counts || {};
  return (
    <div data-testid={`${TID}-result`} className="px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span data-testid={`${TID}-result-version`}
              className="text-[#8A8A93] uppercase tracking-[0.12em]">
          {result.scanner_version}
        </span>
        <span data-testid={`${TID}-result-module-id`}
              className="px-2 py-0.5 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[9px] uppercase tracking-[0.14em] text-[#22D3CC]">
          {result.module_id}
        </span>
        <span data-testid={`${TID}-result-total`} className="text-[#E0E0E0]">
          {result.total_symbols} symbols scanned
        </span>
        <span className="ml-auto inline-flex items-center gap-3">
          {["enter", "watch", "defend", "buyback", "roll_out_up",
            "allow_assignment", "blocked", "no_trade", "error"].map(k => (
            <span key={k} data-testid={`${TID}-result-count-${k}`}
                  className="flex items-center gap-1 text-[#8A8A93]">
              <span className="uppercase tracking-[0.12em]">{k}</span>
              <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
            </span>
          ))}
        </span>
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div data-testid={`${TID}-result-warnings`}
             className="mb-3 text-[10px] font-mono text-[#FFB800]">
          {result.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />{w}
            </div>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => {
          const sym = rec.symbol;
          const localWarnings = perSymbolWarnings[sym] || [];
          const merged = {
            ...rec,
            warnings: [
              ...(Array.isArray(rec.warnings) ? rec.warnings : []),
              ...localWarnings,
            ],
          };
          const rowSnap = (snapshot?.rows || []).find(r => r && r.symbol === sym);
          const rowAge = ageSeconds(snapshot?.as_of);
          return (
            <StrategyResultCard
              key={sym} rec={merged} moduleLabel="CFM_CC_SHARES"
              accent={{
                border: "border-[#22D3CC]/30",
                glow:   "bg-[#22D3CC]/5",
                text:   "text-[#22D3CC]",
              }}
              testidPrefix={`${TID}-result-row`}
            >
              <div
                data-testid={`${TID}-result-row-snapshot-${sym}`}
                className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]"
              >
                snapshot {snapshot?.snapshot_id?.slice(0, 8) || "—"} ·
                {" "}{rowAge != null ? `${rowAge}s ago` : "—"} ·
                {" "}SRC {rowSnap?.source || "—"} ·
                {" "}EXP {rowSnap?.chosen_expiration || "—"}
              </div>
              <CFMSharesMetrics
                rec={merged}
                testidPrefix={`${TID}-result-row`}
              />
            </StrategyResultCard>
          );
        })}
      </ul>

      <details
        data-testid={`${TID}-result-request-json`}
        className="mt-3 border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Request payload used (read-only)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-64 overflow-auto">
          {JSON.stringify(lastRequest, null, 2)}
        </pre>
      </details>
    </div>
  );
}
